/**
 * WooDrawingsModal — displays drawing images from WooCommerce (metadata.woo_drawings).
 * Open, Copy Link, and Download actions for each drawing.
 */

import React from 'react';
import { Button } from './Button';
import { useToast } from '../../hooks/useToast';
import { ExternalLink, Copy, Download } from 'lucide-react';

type DrawingItem = { type_code?: number; label?: string; url?: string };

export const WooDrawingsModal: React.FC<{
  drawings: DrawingItem[];
  onClose: () => void;
}> = ({ drawings, onClose }) => {
  const toast = useToast();
  const valid = drawings.filter(d => d?.url);

  const handleCopyLink = async (url: string, label: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link Copied', `${label || 'Drawing'} link copied to clipboard`);
    } catch {
      toast.error('Copy Failed', 'Could not copy link to clipboard');
    }
  };

  const handleDownload = (url: string, label: string) => {
    const ext = url.split('.').pop()?.split(/[?#]/)[0] || 'pdf';
    const filename = `${(label || 'drawing').replace(/\s+/g, '-')}.${ext}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="fixed inset-0 z-[55] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">Website Drawings</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {valid.length === 0 ? (
            <div className="text-sm text-gray-500 py-8 text-center">No drawings available.</div>
          ) : (
            <ul className="space-y-3">
              {valid.map((d, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between gap-4 p-4 rounded-lg border border-gray-200 bg-gray-50/50 hover:bg-gray-50 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-gray-900">{d.label || 'Drawing'}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => window.open(d.url!, '_blank', 'noopener,noreferrer')}
                      className="gap-1.5"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Open
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleCopyLink(d.url!, d.label || 'Drawing')}
                      className="gap-1.5"
                    >
                      <Copy className="w-3.5 h-3.5" />
                      Copy Link
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleDownload(d.url!, d.label || 'Drawing')}
                      className="gap-1.5"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
